import loadable from '@loadable/component'

import { I18nShape } from '@tribeplatform/react-components/i18n'
import {
  BlockType,
  RegisteredBlocks,
} from '@tribeplatform/react-slate-kit/types'

import { CollectionPage } from './components/Collection/CollectionPage'
import { NotFound } from './components/Error/NotFound'
import { MembersBlock as LegacyMembers } from './components/LegacyMembersBlock'
import { MembersConfig as LegacyMembersConfig } from './components/LegacyMembersBlock/config'
import { MembersBlock as Members } from './components/MembersBlock'
import { MembersConfig } from './components/MembersBlock/config'
import { Navbar } from './components/Navbar'
import { NavBarConfig } from './components/Navbar/NavBarConfig'
import { PostsBlock as Posts } from './components/PostsBlock'
import { PostsConfig } from './components/PostsBlock/config'
import { QuickLinksBlock } from './components/QuickLinksBlock'
import { QuickLinksConfig } from './components/QuickLinksBlock/config'
import { SinglePostBlock } from './components/SinglePostBlock'
import { SinglePostConfig } from './components/SinglePostBlock/config'
import { SpaceHeaderConfig } from './components/Space/SpaceHeader/config'
import { SpaceHeaderSettings } from './components/Space/SpaceHeader/Settings'
import { SpacesListBlock } from './components/SpacesListBlock'
import { SpacesListConfig } from './components/SpacesListBlock/config'
import { Tabs } from './components/Tabs'
import { TagsBlockConfig } from './components/TagsBlock/config'
import { RegisteredFeedBlocks } from './modified-blocks/Feed/registered'
import { RegisteredPostListBlocks } from './modified-blocks/PostList/registered'

const MembersSettings = loadable(
  () => import('./components/MembersBlock/Settings'),
  {
    resolveComponent: m => m.MemberBlockSettings,
  },
)

const PostsSettings = loadable(
  () => import('./components/PostsBlock/Settings'),
  {
    resolveComponent: m => m.PostsBlockSettings,
  },
)

const SinglePostSettings = loadable(
  () => import('./components/SinglePostBlock/Settings'),
  {
    resolveComponent: m => m.SinglePostSettings,
  },
)

const QuickLinksSettings = loadable(
  () => import('./components/QuickLinksBlock/Settings'),
  {
    resolveComponent: m => m.QuickLinksSettings,
  },
)

const SpacesListSettings = loadable(
  () => import('./components/SpacesListBlock/Settings'),
  {
    resolveComponent: m => m.SpacesListSettings,
  },
)

const NotificationList = loadable(
  () => import('./components/Notification/NotificationList'),
  {
    resolveComponent: m => m.NotificationList,
  },
)
const Search = loadable(() => import('./components/Search/Search'), {
  resolveComponent: m => m.Search,
})
const Settings = loadable(() => import('./components/Settings'), {
  resolveComponent: m => m.Settings,
})
const Shaman = loadable(() => import('./components/Shaman'), {
  resolveComponent: m => m.Shaman,
})

const SpaceHeader = loadable(() => import('./components/Space/SpaceHeader'), {
  resolveComponent: m => m.SpaceHeader,
})
const SpaceList = loadable(() => import('./components/SpaceList'), {
  resolveComponent: m => m.SpaceList,
})
const Tags = loadable(() => import('./components/TagsBlock'), {
  resolveComponent: m => m.TagsBlock,
})

const TagsSettings = loadable(() => import('./components/TagsBlock/Settings'), {
  resolveComponent: m => m.TagBlockSettings,
})

const ForgotPassword = loadable(() => import('./components/Auth'), {
  resolveComponent: m => m.ForgotPassword,
})
const Login = loadable(() => import('./components/Auth'), {
  resolveComponent: m => m.Login,
})
const ResetPassword = loadable(() => import('./components/Auth'), {
  resolveComponent: m => m.ResetPassword,
})
const Verify = loadable(() => import('./components/Auth'), {
  resolveComponent: m => m.Verify,
})

const MemberAbout = loadable(() => import('./components/Member/MemberAbout'), {
  resolveComponent: m => m.MemberAbout,
})

const MemberHeader = loadable(
  () => import('./components/Member/MemberHeader'),
  {
    resolveComponent: m => m.MemberHeader,
  },
)

const MemberPage = loadable(() => import('./components/Member/MemberPage'), {
  resolveComponent: m => m.MemberPage,
})

const MemberOnboarding = loadable(
  () => import('./components/MemberOnboarding'),
  {
    resolveComponent: components => components.MemberOnboarding,
  },
)

const NavbarSettings = loadable(
  () => import('./components/Navbar/settings/NavbarSettings'),
  {
    resolveComponent: components => components.NavbarSettings,
  },
)

const PostPage = loadable(() => import('./components/Post/PostPage'), {
  resolveComponent: components => components.PostPage,
})

const SignUp = loadable(() => import('./components/Auth/SignUp'), {
  resolveComponent: components => components.SignUp,
})

export const getRegisteredBlocks = (intl: I18nShape): RegisteredBlocks => {
  const { $t } = intl
  return {
    Feed: RegisteredFeedBlocks(intl),
    PostList: RegisteredPostListBlocks(intl),

    CollectionPage: {
      recommendedName: 'CollectionPage',
      Component: CollectionPage,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Collection page',
          id: 'Blocks.CollectionPage.DisplayName',
        }),
      },
    },
    ForgotPassword: {
      recommendedName: 'ForgotPassword',
      Component: ForgotPassword,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Forgot password',
          id: 'Blocks.ForgotPassword.DisplayName',
        }),
      },
    },
    ResetPassword: {
      recommendedName: 'ResetPassword',
      Component: ResetPassword,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Reset password',
          id: 'Blocks.ResetPassword.DisplayName',
        }),
      },
    },
    Login: {
      recommendedName: 'Login',
      Component: Login,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Login',
          id: 'Blocks.Login.DisplayName',
        }),
      },
    },
    PostPage: {
      recommendedName: 'PostPage',
      Component: PostPage,
      config: {
        type: BlockType.Layout,
        removable: false,
        displayName: $t({
          defaultMessage: 'Post page',
          id: 'Blocks.PostPAge.DisplayName',
        }),
      },
    },
    MemberAbout: {
      recommendedName: 'MemberAbout',
      Component: MemberAbout,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'About member',
          id: 'Blocks.MemberAbout.DisplayName',
        }),
      },
    },
    MemberHeader: {
      recommendedName: 'MemberHeader',
      Component: MemberHeader,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Member header',
          id: 'Blocks.MemberHeader.DisplayName',
        }),
      },
    },
    MemberOnboarding: {
      recommendedName: 'MemberOnboarding',
      Component: MemberOnboarding,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Member Onboarding',
          id: 'Blocks.MemberOnboarding.DisplayName',
        }),
      },
    },
    MemberPage: {
      recommendedName: 'MemberPage',
      Component: MemberPage,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Member page',
          id: 'Blocks.MemberPage.DisplayName',
        }),
      },
    },
    Members: {
      recommendedName: 'Members',
      Component: LegacyMembers,
      config: {
        type: BlockType.Block,
        usable: false,
        ...LegacyMembersConfig(intl),
      },
    },
    MembersBlock: {
      recommendedName: 'Members',
      Component: Members,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: MembersSettings,
        fullSidebarSettings: true,
        ...MembersConfig(intl),
      },
    },
    Posts: {
      recommendedName: 'Posts',
      Component: Posts,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: PostsSettings,
        fullSidebarSettings: true,
        ...PostsConfig(intl),
      },
    },
    SinglePost: {
      recommendedName: 'Single post',
      Component: SinglePostBlock,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: SinglePostSettings,
        fullSidebarSettings: true,
        ...SinglePostConfig(intl),
      },
    },
    NewQuickLinks: {
      recommendedName: 'Quick links',
      Component: QuickLinksBlock,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: QuickLinksSettings,
        fullSidebarSettings: true,
        ...QuickLinksConfig(intl),
      },
    },
    SpacesList: {
      recommendedName: 'Space list',
      Component: SpacesListBlock,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: SpacesListSettings,
        fullSidebarSettings: true,
        ...SpacesListConfig(intl),
      },
    },
    Navbar: {
      recommendedName: 'Navbar',
      Component: Navbar,
      config: {
        Settings: NavbarSettings,
        ...NavBarConfig(intl),
      },
    },
    NotificationList: {
      recommendedName: 'NotificationList',
      Component: NotificationList,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Notifications list',
          id: 'Blocks.NotificationsList.DisplayName',
        }),
      },
    },
    NotFound: {
      recommendedName: 'NotFound',
      Component: NotFound,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Tabs',
          id: 'Blocks.NotFound.DisplayName',
        }),
      },
    },
    SignUp: {
      recommendedName: 'SignUp',
      Component: SignUp,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Sign up',
          id: 'Blocks.SignUp.DisplayName',
        }),
      },
    },
    Search: {
      recommendedName: 'Search',
      Component: Search,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Search',
          id: 'Blocks.Search.DisplayName',
        }),
      },
    },
    Settings: {
      recommendedName: 'Settings',
      Component: Settings,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Settings',
          id: 'Blocks.Settings.DisplayName',
        }),
      },
    },
    Shaman: {
      recommendedName: 'Shaman',
      Component: Shaman,
      config: { type: BlockType.Block, removable: false },
    },
    SpaceHeader: {
      recommendedName: 'SpaceHeader',
      Component: SpaceHeader,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: SpaceHeaderSettings,
        ...SpaceHeaderConfig(intl),
      },
    },
    SpaceList: {
      recommendedName: 'SpaceList',
      Component: SpaceList,
      config: {
        type: BlockType.Block,
        usable: false,
        displayName: $t({
          defaultMessage: 'Spaces',
          id: 'Blocks.SpaceList.DisplayName',
        }),
      },
    },
    Tabs: {
      recommendedName: 'Tabs',
      Component: Tabs,
      config: {
        type: BlockType.Block,
        removable: true,
        acceptsChildren: true,
        lockedChildren: true,
        displayName: $t({
          defaultMessage: 'Tabs',
          id: 'Blocks.Tabs.DisplayName',
        }),
      },
    },
    Tags: {
      recommendedName: 'Tags',
      Component: Tags,
      config: {
        type: BlockType.Block,
        usable: true,
        Settings: TagsSettings,
        ...TagsBlockConfig(intl),
      },
    },
    Verify: {
      recommendedName: 'Verify',
      Component: Verify,
      config: {
        type: BlockType.Block,
        removable: false,
        displayName: $t({
          defaultMessage: 'Verify',
          id: 'Blocks.Verify.DisplayName',
        }),
      },
    },
  }
}
